import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/Navbar';

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : '';
    let createdMeta = null;

    document.title = title;
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      createdMeta = document.createElement('meta');
      createdMeta.name = 'description';
      createdMeta.content = description;
      document.head.appendChild(createdMeta);
    }

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        createdMeta.remove();
      } else if (meta) {
        meta.setAttribute('content', prevDesc || '');
      }
    };
  }, [title, description]);
}

export default function WhySpreadsheetsFail() {
  const { t } = useTranslation();

  usePageMeta(
    t('blog.whySpreadsheetsFail.pageTitle'),
    t('blog.whySpreadsheetsFail.pageDescription')
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="mb-10 text-center">
          <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">{t('blog.label')}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">{t('blog.whySpreadsheetsFail.heading')}</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {t('blog.whySpreadsheetsFail.intro')}
          </p>
        </header>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <p className="text-gray-700 mb-4">
            {t('blog.whySpreadsheetsFail.commonTools')}
          </p>
          <p className="text-gray-700">
            {t('blog.whySpreadsheetsFail.butWhen')}
          </p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">1️⃣ {t('blog.whySpreadsheetsFail.section1Title')}</h2>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section1Rely')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.section1Rely1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section1Rely2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section1Rely3')}</li>
          </ul>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section1Reality')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2">
            <li>{t('blog.whySpreadsheetsFail.section1Reality1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section1Reality2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section1Reality3')}</li>
          </ul>
          <p className="text-gray-700 mt-4">{t('blog.whySpreadsheetsFail.section1Conclusion')}</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">2️⃣ {t('blog.whySpreadsheetsFail.section2Title')}</h2>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section2Intro')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.section2Struggle1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section2Struggle2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section2Struggle3')}</li>
          </ul>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section2EndsUp')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2">
            <li>{t('blog.whySpreadsheetsFail.section2EndsUp1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section2EndsUp2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section2EndsUp3')}</li>
          </ul>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">3️⃣ {t('blog.whySpreadsheetsFail.section3Title')}</h2>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section3Intro')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.section3Problem1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section3Problem2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section3Problem3')}</li>
          </ul>
          <p className="text-gray-700">{t('blog.whySpreadsheetsFail.section3Conclusion')}</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">4️⃣ {t('blog.whySpreadsheetsFail.section4Title')}</h2>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.section4Intro')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.section4Problem1')}</li>
            <li>{t('blog.whySpreadsheetsFail.section4Problem2')}</li>
            <li>{t('blog.whySpreadsheetsFail.section4Problem3')}</li>
          </ul>
          <p className="text-gray-700">{t('blog.whySpreadsheetsFail.section4Conclusion')}</p>
        </section>

        <section className="bg-gradient-to-br from-gray-50 via-emerald-50 to-white border border-emerald-100 rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('blog.whySpreadsheetsFail.whatWorksBetter')}</h2>
          <p className="text-gray-700 mb-4">{t('blog.whySpreadsheetsFail.whatWorksBetterIntro')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.whatWorksBetter1')}</li>
            <li>{t('blog.whySpreadsheetsFail.whatWorksBetter2')}</li>
            <li>{t('blog.whySpreadsheetsFail.whatWorksBetter3')}</li>
            <li>{t('blog.whySpreadsheetsFail.whatWorksBetter4')}</li>
          </ul>
          <p className="text-gray-700">{t('blog.whySpreadsheetsFail.whatWorksBetterConclusion')}</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('blog.whySpreadsheetsFail.howReduceRisk')}</h2>
          <p className="text-gray-700 mb-4">{t('blog.whySpreadsheetsFail.howReduceRiskIntro')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2">
            <li>{t('blog.whySpreadsheetsFail.howReduceRisk1')}</li>
            <li>{t('blog.whySpreadsheetsFail.howReduceRisk2')}</li>
            <li>{t('blog.whySpreadsheetsFail.howReduceRisk3')}</li>
            <li>{t('blog.whySpreadsheetsFail.howReduceRisk4')}</li>
          </ul>
          <p className="text-gray-700 mt-4">{t('blog.whySpreadsheetsFail.howReduceRiskConclusion')}</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('blog.whySpreadsheetsFail.howBMCHelps')}</h2>
          <p className="text-gray-700 mb-4">{t('blog.whySpreadsheetsFail.howBMCHelpsIntro')}</p>
          <p className="text-gray-700 mb-3">{t('blog.whySpreadsheetsFail.howBMCHelpsWith')}</p>
          <ul className="list-disc list-inside text-gray-800 space-y-2 mb-4">
            <li>{t('blog.whySpreadsheetsFail.howBMCHelps1')}</li>
            <li>{t('blog.whySpreadsheetsFail.howBMCHelps2')}</li>
            <li>{t('blog.whySpreadsheetsFail.howBMCHelps3')}</li>
          </ul>
          <div className="flex flex-wrap gap-3 mt-4">
            <a href="/digital-inspection-checklists" className="inline-flex items-center px-5 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">{t('blog.whySpreadsheetsFail.exploreDigital')}</a>
            <a href="/compliance-checklist-software-uk" className="inline-flex items-center px-5 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">{t('blog.whySpreadsheetsFail.seeCompliance')}</a>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('blog.whySpreadsheetsFail.conclusionTitle')}</h2>
          <p className="text-gray-700 mb-4">{t('blog.whySpreadsheetsFail.conclusion1')}</p>
          <p className="text-gray-700">{t('blog.whySpreadsheetsFail.conclusion2')}</p>
        </section>
      </main>
    </div>
  );
}
